// Research Workflow Type Definitions

import { ActiveWork } from "../services/researchApi";

export type WorkflowStage =
  | "requirements"
  | "longList"
  | "shortList"
  | "finalSelection";

export type WorkflowStatus =
  | "candidate"
  | "approved"
  | "rejected"
  | "shortlisted"
  | "final";

export type StageStatus = "not_started" | "in_progress" | "ready" | "complete";

export type DeepResearchStatus = "not_started" | "in_progress" | "complete";

// Per-candidate evaluation execution status (#888).
export type EvaluationStatus =
  | "none"
  | "queued"
  | "running"
  | "completed"
  | "failed"
  | "cancelled";

export type AgentState = "idle" | "running" | "paused";

export type MonitoringFrequency = "daily" | "weekly";

export type CriterionMode = "strict" | "fuzzy";

export interface FuzzyRubric {
  anchors: Record<string, string>; // e.g. {"1": "...", "2": "...", ..., "5": "..."}
  scale_min: number;
  scale_max: number;
}

export interface Criterion {
  id: string;
  label: string;
  value: string;
  weight: 0 | 1 | 2 | 3 | 4 | 5;
  mode: CriterionMode;
  rubric?: FuzzyRubric;
}

export interface Requirements {
  objective: string;
  criteria: Criterion[];
}

export interface CriterionScore {
  criterionId: string;
  criterionLabel: string;
  score: number; // 0-10
  confidence: "low" | "medium" | "high";
  evidence: string[];
}

export interface DeepResearchReport {
  executiveSummary: string;
  criterionScores: CriterionScore[];
  gaps: string[];
  unknowns: string[];
  lastRun: string; // ISO timestamp
}

export interface MonitoringConfig {
  enabled: boolean;
  signalTypes: string[]; // e.g., ['contracts', 'facilities', 'ownership', 'programs']
  frequency: MonitoringFrequency;
}

export interface ResearchEntity {
  // Base entity fields
  id: string;
  kg_entity_id: string; // Knowledge graph entity ID for detail overlay
  name: string;
  type: "organization" | "facility" | "project";
  description?: string;
  // Live org status from the KG (computed server-side on every read):
  // true when status labels mark the org as dissolved/defunct.
  inactive?: boolean;

  // Entity-specific fields
  home_country?: string;
  country_iso2?: string;
  founding_year?: number;
  facility_type?: string | string[];
  project_type?: string | string[];
  tags?: string[];

  // Research workflow fields
  relevanceScore?: number; // 0-100
  matchCategory?:
    | "high_potential"
    | "medium_potential"
    | "low_potential"
    | "irrelevant"
    | "manual";
  /** Which discovery lane found this candidate ("graph" | "web"). */
  discoverySource?: string;
  evaluationReasoning?: string; // Reasoning for relevance score (legacy)
  evaluationData?: {
    decision?: "accept" | "reject" | "uncertain";
    confidence?: number;
    match_score?: number;
    match_signals?: string[];
    concerns?: string[];
    rejection_reason?: string | null;
    overall_score?: number;
    schema_version?: string;
    spec_version_changed?: boolean;
    eval_spec_version_number?: number;
    latest_spec_version_number?: number;
    criteria_results?: Record<
      string,
      {
        score: number;
        one_sentence_summary?: string;
        confidence: number;
        evidence_count?: number;
        label?: string;
        description?: string;
        weight?: number;
        is_stale?: boolean;
        stale_reason?: string;
      }
    >;
    executive_summary?: {
      overall_assessment: string;
      key_strengths: string[];
      key_concerns: string[];
      recommendation: string;
    };
  };
  latest_reference?: {
    id: string;
    relevance_score?: number;
    discovery_meta?: {
      match_score?: number;
      match_category?: string;
      match_signals?: string[];
      concerns?: string[];
      rejection_reason?: string | null;
      confidence?: number;
      decision?: string;
      schema_version?: string;
      raw_llm_response?: string;
      [key: string]: any;
    };
    discovered_at?: string;
    spec_version_id?: string | null;
    spec_version_number?: number | null;
  };
  specVersionId?: string | null;
  specVersionNumber?: number | null;
  workflowStatus: WorkflowStatus;
  rejectionReason?: string;
  deepResearchStatus: DeepResearchStatus;
  deepResearchReport?: DeepResearchReport;
  monitoringConfig?: MonitoringConfig;

  // Per-candidate evaluation execution status + its task/run ids (#888).
  evaluationStatus?: EvaluationStatus;
  evaluationTaskId?: string | null;
  evaluationRunId?: string | null;
  evaluationRunAt?: string | null; // Date/time of the last evaluation run (start)
  documentCount?: number; // Documents linked to this candidate (#1013)

  // Metadata
  addedToWorkflowDate: string; // ISO timestamp
  lastUpdated: string; // ISO timestamp
}

export interface AgentActivity {
  id: string;
  timestamp: string; // ISO timestamp
  stage: WorkflowStage;
  action: string;
  details: string;
  entityId?: string;
  userUsername?: string;
  userEmail?: string;
}

export interface StageInfo {
  stage: WorkflowStage;
  status: StageStatus;
  counts: {
    total?: number;
    approved?: number;
    rejected?: number;
    shortlisted?: number;
    evaluated?: number; // shortlisted candidates whose latest evaluation settled
    final?: number;
  };
}

export interface WorkflowState {
  // Project metadata
  projectTitle: string;
  requirements: Requirements;

  // Entities
  entities: ResearchEntity[];

  // Agent state
  agentState: AgentState;
  agentActivities: AgentActivity[];
  // Candidate-centric in-flight summary (#888)
  activeWork: ActiveWork;

  // Stage information
  stages: {
    requirements: StageInfo;
    longList: StageInfo;
    shortList: StageInfo;
    finalSelection: StageInfo;
  };

  // UI state
  currentStage: WorkflowStage;
  selectedEntityId?: string;
  overlayMode?: "profile" | "research";
}

export interface RejectionReason {
  id: string;
  label: string;
  description?: string;
}

export const REJECTION_REASONS: RejectionReason[] = [
  {
    id: "wrong_geography",
    label: "Wrong Geography",
    description: "Not in target region",
  },
  {
    id: "wrong_capability",
    label: "Wrong Capability/Product",
    description: "Does not match required capabilities",
  },
  {
    id: "too_small",
    label: "Too Small / Not Relevant Scale",
    description: "Organization size below threshold",
  },
  {
    id: "competitor_alignment",
    label: "Existing Competitor Alignment",
    description: "Already aligned with competitors",
  },
  {
    id: "not_customer_type",
    label: "Not a Customer Type",
    description: "Does not fit customer profile",
  },
  {
    id: "insufficient_data",
    label: "Insufficient Data",
    description: "Not enough information available",
  },
];

export const MONITORING_SIGNAL_TYPES = [
  { id: "contracts", label: "Contracts & Awards" },
  { id: "facilities", label: "Facility Changes" },
  { id: "ownership", label: "Ownership Changes" },
  { id: "programs", label: "Program Announcements" },
  { id: "partnerships", label: "Partnerships & Alliances" },
  { id: "leadership", label: "Leadership Changes" },
];
